import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import { Plus, Trash2, Sparkles, Lock, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = ["USD", "EUR", "GBP", "INR", "CAD", "AUD"];

// Human labels for the fixed Schedule C category list (kept in sync with
// EXPENSE_CATEGORIES in backend/models.py). Order here also drives the
// dropdown ordering, so uncategorized ("Other") stays at the bottom.
const CATEGORY_LABELS = {
  advertising: "Advertising",
  car_and_truck: "Car & truck",
  commissions_fees: "Commissions & fees",
  contract_labor: "Contract labor",
  insurance: "Insurance",
  legal_professional: "Legal & professional",
  office_expense: "Office expense",
  rent_lease: "Rent / lease",
  repairs_maintenance: "Repairs & maintenance",
  supplies: "Supplies",
  taxes_licenses: "Taxes & licenses",
  travel: "Travel",
  meals: "Meals (50%)",
  utilities: "Utilities",
  software_subscriptions: "Software & subscriptions",
  home_office: "Home office",
  bank_fees: "Bank & network fees",
  education: "Education",
  other: "Other",
};

const CATEGORY_ORDER = Object.keys(CATEGORY_LABELS);

const todayIso = () => new Date().toISOString().slice(0, 10);

const Expenses = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const tier = (user?.subscription_tier || "free").toLowerCase();
  const isPro = tier === "pro" || tier === "team" || tier === "studio";

  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [rowBusy, setRowBusy] = useState({}); // { expense_id: true }
  const [form, setForm] = useState({
    date: todayIso(),
    vendor: "",
    description: "",
    amount: "",
    currency: "USD",
    category: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [listR, sumR] = await Promise.all([
        api.get("/expenses"),
        api.get("/expenses/summary"),
      ]);
      setExpenses(listR.data || []);
      setSummary(sumR.data || null);
    } catch (e) {
      toast.error("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const uncategorizedIds = useMemo(
    () => expenses.filter((e) => (e.category || "other") === "other" && !e.ai_categorized).map((e) => e.expense_id),
    [expenses]
  );

  const addExpense = async (e) => {
    e.preventDefault();
    if (!form.vendor.trim() || !form.amount || !form.date) {
      toast.error("Vendor, amount and date are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/expenses", {
        date: form.date,
        vendor: form.vendor.trim(),
        description: form.description.trim(),
        amount: Number(form.amount),
        currency: form.currency,
        category: form.category || null,
      });
      setForm({ date: todayIso(), vendor: "", description: "", amount: "", currency: form.currency, category: "" });
      toast.success("Expense added");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add expense");
    } finally {
      setSaving(false);
    }
  };

  const categorizeOne = async (exp) => {
    if (!isPro) {
      navigate("/pricing");
      return;
    }
    setRowBusy((s) => ({ ...s, [exp.expense_id]: true }));
    try {
      const r = await api.post("/ai/categorize-expense", {
        vendor: exp.vendor,
        description: exp.description || "",
        amount: exp.amount,
        currency: exp.currency,
      });
      const d = r.data || {};
      await api.patch(`/expenses/${exp.expense_id}`, {
        category: d.category,
        deductible: d.deductible,
      });
      // We call the patch endpoint which clears ai_categorized — override
      // locally so the AI badge & reasoning still surface.
      setExpenses((prev) =>
        prev.map((e) =>
          e.expense_id === exp.expense_id
            ? { ...e, category: d.category, deductible: d.deductible, ai_categorized: true, ai_confidence: d.confidence, ai_reasoning: d.reasoning }
            : e
        )
      );
      toast.success(`Categorized as ${CATEGORY_LABELS[d.category] || d.category}`);
      // Refresh summary in background
      api.get("/expenses/summary").then((s) => setSummary(s.data)).catch(() => {});
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error("Upgrade to Pro for AI categorization");
        navigate("/pricing");
      } else {
        toast.error(err?.response?.data?.detail || "AI categorization failed");
      }
    } finally {
      setRowBusy((s) => ({ ...s, [exp.expense_id]: false }));
    }
  };

  const categorizeAll = async () => {
    if (!isPro) { navigate("/pricing"); return; }
    if (!uncategorizedIds.length) {
      toast.info("Nothing to categorize");
      return;
    }
    setBulkLoading(true);
    try {
      const r = await api.post("/ai/categorize-expense-bulk", { expense_ids: uncategorizedIds });
      const updatedCount = (r.data?.updated || []).length;
      toast.success(`AI categorized ${updatedCount} expense${updatedCount === 1 ? "" : "s"}`);
      await load();
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error("Upgrade to Pro for bulk AI categorization");
        navigate("/pricing");
      } else {
        toast.error(err?.response?.data?.detail || "Bulk categorization failed");
      }
    } finally {
      setBulkLoading(false);
    }
  };

  const updateCategory = async (exp, category) => {
    try {
      await api.patch(`/expenses/${exp.expense_id}`, { category });
      setExpenses((prev) =>
        prev.map((e) => (e.expense_id === exp.expense_id ? { ...e, category, ai_categorized: false } : e))
      );
      api.get("/expenses/summary").then((s) => setSummary(s.data)).catch(() => {});
    } catch {
      toast.error("Failed to update category");
    }
  };

  const removeExpense = async (id) => {
    try {
      await api.delete(`/expenses/${id}`);
      setExpenses((p) => p.filter((e) => e.expense_id !== id));
      api.get("/expenses/summary").then((s) => setSummary(s.data)).catch(() => {});
      toast.success("Expense removed");
    } catch {
      toast.error("Failed to remove");
    }
  };

  return (
    <div data-testid="expenses-page">
      <PageHeader
        overline="Bookkeeping"
        title="Expenses"
        description="Track deductible business expenses. Let AI sort them into IRS Schedule C categories."
        actions={
          <button
            onClick={categorizeAll}
            disabled={bulkLoading || !uncategorizedIds.length}
            data-testid="ai-categorize-all-btn"
            className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50"
            style={{
              background: isPro
                ? "linear-gradient(180deg, hsl(var(--quill-info)) 0%, hsl(var(--quill-warm)) 100%)"
                : "transparent",
              color: isPro ? "hsl(var(--quill-ink-warm))" : "hsl(var(--quill-warm))",
              border: isPro ? "none" : "1px solid hsl(var(--quill-warm) / 0.4)",
            }}
          >
            {bulkLoading ? <Loader2 size={14} className="animate-spin" /> : isPro ? <Wand2 size={14} /> : <Lock size={14} />}
            {bulkLoading ? "Categorizing…" : isPro ? `AI categorize (${uncategorizedIds.length})` : "AI categorize — Pro"}
          </button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-[#2c241e]">
        <StatCard
          label="Total expenses"
          value={fmtMoney(summary?.total || 0)}
          sub={`Year ${summary?.year || new Date().getFullYear()}`}
          testId="exp-total"
        />
        <StatCard
          label="Deductible"
          value={fmtMoney(summary?.deductible || 0)}
          sub="Schedule C"
          accent="success"
          testId="exp-deductible"
        />
        <StatCard
          label="Uncategorized"
          value={uncategorizedIds.length.toString()}
          sub={uncategorizedIds.length ? "Awaiting AI" : "All sorted"}
          accent={uncategorizedIds.length ? "warning" : undefined}
          testId="exp-uncategorized"
        />
        <StatCard
          label="Categories used"
          value={Object.keys(summary?.by_category || {}).length.toString()}
          sub="This year"
          testId="exp-cat-count"
        />
      </div>

      <div className="p-4 md:p-8 grid md:grid-cols-12 gap-8">
        {/* Add expense form */}
        <form onSubmit={addExpense} className="md:col-span-4 border border-[#2c241e] p-6 h-fit" data-testid="expense-form">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-5">Add expense</div>
          <div className="space-y-4">
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Date</div>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="expense-date-input"
                className="w-full bg-[#0A0A0A] border border-[#2c241e] px-3 py-2.5 text-sm focus:outline-none focus:border-white"
                required
              />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Vendor</div>
              <input
                value={form.vendor}
                onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                data-testid="expense-vendor-input"
                placeholder="e.g. Adobe, Notion, Delta Airlines"
                className="w-full bg-[#0A0A0A] border border-[#2c241e] px-3 py-2.5 text-sm focus:outline-none focus:border-white"
                required
              />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Description (optional)</div>
              <input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="expense-description-input"
                placeholder="Photoshop annual, flight to NYC…"
                className="w-full bg-[#0A0A0A] border border-[#2c241e] px-3 py-2.5 text-sm focus:outline-none focus:border-white"
              />
            </label>
            <div className="grid grid-cols-3 gap-3">
              <label className="col-span-2 block">
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Amount</div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  data-testid="expense-amount-input"
                  placeholder="0.00"
                  className="w-full bg-[#0A0A0A] border border-[#2c241e] px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-white"
                  required
                />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Ccy</div>
                <select
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  data-testid="expense-currency-select"
                  className="w-full bg-[#0A0A0A] border border-[#2c241e] px-3 py-2.5 text-sm focus:outline-none focus:border-white"
                >
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            </div>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Category (optional — AI can suggest)</div>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="expense-category-select"
                className="w-full bg-[#0A0A0A] border border-[#2c241e] px-3 py-2.5 text-sm focus:outline-none focus:border-white"
              >
                <option value="">Let AI decide</option>
                {CATEGORY_ORDER.map((c) => (
                  <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                ))}
              </select>
            </label>
            <button
              type="submit"
              disabled={saving}
              data-testid="expense-add-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-white text-black text-sm font-medium px-4 py-2.5 rounded-md hover:bg-zinc-200 disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {saving ? "Adding…" : "Add expense"}
            </button>
          </div>
        </form>

        {/* Expense list */}
        <div className="md:col-span-8">
          {loading ? (
            <div className="p-10 text-zinc-500 text-sm">Loading…</div>
          ) : expenses.length === 0 ? (
            <div className="border border-[#2c241e] p-10 text-center" data-testid="expenses-empty">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">No expenses yet</div>
              <div className="text-sm text-zinc-400 max-w-md mx-auto">
                Add your first expense on the left. Then hit <span className="text-amber-300">AI categorize</span> and Quill will sort them into Schedule C buckets in seconds.
              </div>
            </div>
          ) : (
            <div className="border border-[#2c241e]" data-testid="expenses-table">
              <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#2c241e] text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                <div className="col-span-2">Date</div>
                <div className="col-span-4">Vendor</div>
                <div className="col-span-2 text-right">Amount</div>
                <div className="col-span-3">Category</div>
                <div className="col-span-1" />
              </div>
              {expenses.map((exp) => {
                const busy = !!rowBusy[exp.expense_id];
                const cat = exp.category || "other";
                return (
                  <div
                    key={exp.expense_id}
                    data-testid={`expense-row-${exp.expense_id}`}
                    className="grid grid-cols-12 gap-x-3 gap-y-2 px-4 py-3 border-b border-[#2c241e] last:border-b-0 items-center text-sm hover:bg-[#1a1410]"
                  >
                    <div className="col-span-4 md:col-span-2 text-zinc-400 text-xs order-1">{fmtDate(exp.date)}</div>
                    <div className="col-span-8 md:col-span-4 min-w-0 order-3 md:order-2">
                      <div className="truncate text-white">{exp.vendor}</div>
                      {exp.description && (
                        <div className="text-[11px] text-zinc-500 truncate">{exp.description}</div>
                      )}
                      {exp.ai_categorized && exp.ai_reasoning && (
                        <div
                          className="text-[10px] mt-0.5 flex items-center gap-1 text-amber-300/80"
                          data-testid={`ai-reasoning-${exp.expense_id}`}
                        >
                          <Sparkles size={10} /> {exp.ai_reasoning}
                        </div>
                      )}
                    </div>
                    <div className="col-span-8 md:col-span-2 text-right font-mono order-2 md:order-3">
                      {fmtMoney(exp.amount, exp.currency)}
                    </div>
                    <div className="col-span-10 md:col-span-3 flex items-center gap-1.5 min-w-0 order-4">
                      <select
                        value={cat}
                        onChange={(e) => updateCategory(exp, e.target.value)}
                        data-testid={`expense-cat-select-${exp.expense_id}`}
                        className="flex-1 min-w-0 bg-[#0A0A0A] border border-[#2c241e] px-2 py-2 md:py-1.5 text-xs focus:outline-none focus:border-white"
                      >
                        {CATEGORY_ORDER.map((c) => (
                          <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => categorizeOne(exp)}
                        disabled={busy}
                        title={isPro ? "AI categorize" : "Upgrade to Pro"}
                        aria-label={isPro ? "AI categorize" : "Upgrade to Pro"}
                        data-testid={`ai-categorize-btn-${exp.expense_id}`}
                        className="p-2.5 shrink-0 rounded-md text-amber-300/80 hover:text-amber-200 hover:bg-amber-500/10 disabled:opacity-50"
                      >
                        {busy ? <Loader2 size={14} className="animate-spin" /> : isPro ? <Sparkles size={14} /> : <Lock size={14} />}
                      </button>
                    </div>
                    <div className="col-span-2 md:col-span-1 flex justify-end order-5">
                      <button
                        onClick={() => removeExpense(exp.expense_id)}
                        data-testid={`expense-remove-${exp.expense_id}`}
                        className="text-zinc-500 hover:text-rose-400 p-2.5 rounded-md hover:bg-rose-500/10"
                        title="Delete"
                        aria-label="Delete expense"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Expenses;
